import React, { useState, useEffect } from "react";
import { Dialog, IconButton } from "@mui/material";
import {
  X,
  User,
  Calendar,
  FileText,
  Paperclip,
  Clock,
  MessageSquare,
  Download,
  AlertCircle,
  CheckCircle,
  XCircle,
  Video,
  ExternalLink,
} from "lucide-react";
import { getSchoolClassLabel } from "../../common/utils";
import { useRequestsApi } from "../../../hooks/useRequestsApi";
import {
  downloadFile,
  getFileDetails,
} from "../../../server/Files CRUD/Requests";
import { GetFileDetailsOutput } from "../../../server/Files CRUD/OutputsTypes";

interface RequestDetailsModalProps {
  open: boolean;
  request: any;
  onClose: () => void;
  getStatusBadgeColor: (status: string) => string;
  getStatusText: (status: string) => string;
}

const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({
  open,
  request,
  onClose,
  getStatusBadgeColor,
  getStatusText,
}) => {
  const [detailedRequest, setDetailedRequest] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<GetFileDetailsOutput[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [meeting, setMeeting] = useState<any>(null);
  const { getRequestById, getMeetingByRequest } = useRequestsApi();

  // Récupérer les détails complets de la demande avec les fichiers
  useEffect(() => {
    if (open && request?._id) {
      const fetchDetailedRequest = async () => {
        try {
          setLoading(true);
          const response = await getRequestById(request._id);
          if (response.status === 200 && response.body) {
            console.log("Réponse API getRequestById:", response.body);
            setDetailedRequest(response.body);

            // Récupérer les fichiers si fileIds existe
            if (
              response.body &&
              "fileIds" in response.body &&
              response.body.fileIds &&
              response.body.fileIds.length > 0
            ) {
              console.log("FileIds trouvés:", response.body.fileIds);
              setFilesLoading(true);

              try {
                // Récupérer les détails de chaque fichier
                const filesData = await Promise.all(
                  response.body.fileIds.map((fileId: string) =>
                    getFileDetails(fileId),
                  ),
                );

                const validFiles = filesData
                  .filter((response: any) => response.status === 200)
                  .map(
                    (response: any) => response.body as GetFileDetailsOutput,
                  );

                setFiles(validFiles);
                console.log("Fichiers récupérés:", validFiles);
              } catch (error) {
                console.error(
                  "Erreur lors de la récupération des fichiers:",
                  error,
                );
              } finally {
                setFilesLoading(false);
              }
            }

            // Récupérer le meeting si meetingId existe
            if (
              response.body &&
              "meetingId" in response.body &&
              response.body.meetingId
            ) {
              console.log("MeetingId trouvé:", response.body.meetingId);
              try {
                const meetingResponse = await getMeetingByRequest(request._id);
                console.log("Réponse meeting:", meetingResponse);
                if (meetingResponse.status === 200 && meetingResponse.body) {
                  setMeeting(meetingResponse.body);
                }
              } catch (meetingError) {
                console.error(
                  "Erreur lors de la récupération du meeting:",
                  meetingError,
                );
              }
            } else {
              console.log("Aucun meetingId trouvé dans la réponse");
            }
          } else {
            // Fallback sur les données de base si l'API échoue
            setDetailedRequest(request);
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des détails:", error);
          // Fallback sur les données de base
          setDetailedRequest(request);
        } finally {
          setLoading(false);
        }
      };

      fetchDetailedRequest();
    }
  }, [open, request?._id, getRequestById, getMeetingByRequest, request]);

  // Utiliser les données détaillées si disponibles, sinon les données de base
  const currentRequest = detailedRequest || request;

  // Fonction de téléchargement automatique
  const handleFileDownload = async (fileId: string, filename: string) => {
    try {
      const blob = await downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement du fichier:", error);
    }
  };

  // Formater la date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Obtenir l'icône du statut
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "validated":
      case "slots_proposed":
      case "student_confirmed":
      case "scheduled":
        return <CheckCircle className="w-4 h-4" />;
      case "rejected":
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        zIndex: 9999,
        "& .MuiDialog-paper": {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header moderne */}
      <div className="bg-[#E3B500] text-white">
        <div className="p-4 sm:p-6 lg:p-8 flex justify-between items-center">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold">
                Détails de mon explication
              </h2>
              <p className="text-sm sm:text-base text-white/90 mt-1">
                Explication mathématique
              </p>
            </div>
          </div>
          <IconButton
            onClick={onClose}
            className="text-white hover:bg-white/20 transition-colors rounded-xl"
            sx={{
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
            }}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </IconButton>
        </div>
      </div>

      {/* Contenu moderne */}
      <div className="bg-gray-50 min-h-[400px] max-h-[70vh] overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E3B500]/20"></div>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E3B500] border-t-transparent absolute top-0 left-0"></div>
              </div>
              <span className="mt-4 text-gray-600 font-medium">
                Chargement des détails...
              </span>
            </div>
          ) : (
            /* Affichage des données simplifié et clair */
            <div className="space-y-6">
              {/* Informations principales */}
              <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {/* Statut */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-[#E3B500]" />
                      <span className="text-sm font-medium text-gray-700">
                        Statut
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${getStatusBadgeColor(
                        currentRequest.status,
                      )}`}
                    >
                      {getStatusIcon(currentRequest.status)}
                      {getStatusText(currentRequest.status)}
                    </span>
                  </div>

                  {/* Classe */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">
                        Classe
                      </span>
                    </div>
                    <p className="text-base font-semibold text-gray-900">
                      {getSchoolClassLabel(
                        currentRequest.schoolClass ||
                          currentRequest.studentClass,
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description du problème */}
              <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-[#E3B500]" />
                  <h3 className="text-lg font-semibold text-gray-800">
                    Problème à résoudre
                  </h3>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {currentRequest.description ||
                      currentRequest.comment ||
                      "Aucune description du problème"}
                  </p>
                </div>
              </div>

              {/* Fichiers joints */}
              {currentRequest.fileIds && currentRequest.fileIds.length > 0 && (
                <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-[#E3B500]" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      Fichiers joints
                    </h3>
                    <span className="bg-[#E3B500] text-white px-2 py-1 rounded-full text-xs font-medium">
                      {currentRequest.fileIds.length}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {filesLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#E3B500] border-t-transparent"></div>
                        <span className="ml-2 text-sm text-gray-600">
                          Chargement...
                        </span>
                      </div>
                    ) : files && files.length > 0 ? (
                      files.map((file: GetFileDetailsOutput) => (
                        <div
                          key={file._id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p
                                className="text-sm font-medium text-gray-900 truncate"
                                title={file.originalName}
                              >
                                {file.originalName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {file.mimeType} •{" "}
                                {formatFileSize(file.size || 0)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleFileDownload(file._id, file.originalName)
                            }
                            className="p-2 text-[#E3B500] hover:bg-[#E3B500]/10 rounded-lg transition-colors"
                            title="Télécharger"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Aucun fichier disponible
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Informations de la séance */}
              {(currentRequest.status === "student_confirmed" ||
                currentRequest.status === "meeting_scheduled" ||
                currentRequest.status === "scheduled" ||
                (meeting && meeting.link) ||
                currentRequest.chosenSlot) && (
                <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Video className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      Informations de la séance
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {/* Créneau choisi */}
                    {currentRequest.chosenSlot && (
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <Clock className="w-4 h-4 text-green-600" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-900 mb-1">
                            Créneau confirmé
                          </p>
                          <p className="text-sm text-green-800">
                            {new Date(
                              currentRequest.chosenSlot.dateTime,
                            ).toLocaleDateString("fr-FR", {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Lien Meet */}
                    {meeting && meeting.link ? (
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-blue-900 mb-1">
                            Lien Google Meet
                          </p>
                          <p className="text-sm text-blue-800 truncate">
                            {meeting.link}
                          </p>
                        </div>
                        <a
                          href={meeting.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Ouvrir le lien Meet"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    ) : (
                      (currentRequest.status === "student_confirmed" ||
                        currentRequest.status === "meeting_scheduled" ||
                        currentRequest.status === "scheduled") && (
                        <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <p className="text-sm font-medium text-yellow-900">
                            Le lien Meet sera ajouté par votre enseignant avant
                            la séance
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Professeur assigné */}
              {currentRequest.teacher && (
                <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      Professeur assigné
                    </h3>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <User className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-base font-semibold text-green-900">
                        {currentRequest.teacher.firstname}{" "}
                        {currentRequest.teacher.lastname}
                      </p>
                      <p className="text-sm text-green-700">
                        {currentRequest.teacher.email}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Raison de rejet */}
              {currentRequest.status === "rejected" &&
                currentRequest.rejectionReason && (
                  <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                    <div className="flex items-center gap-2 mb-4">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <h3 className="text-lg font-semibold text-gray-800">
                        Raison du rejet
                      </h3>
                    </div>

                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-800">
                        {currentRequest.rejectionReason}
                      </p>
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>

      {/* Footer moderne */}
      <div className="bg-gray-100 border-t border-gray-200 p-4 sm:p-6">
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="group px-6 py-3 bg-[#E3B500] text-white rounded-xl flex items-center gap-2 hover:bg-[#CA8A04] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium text-sm sm:text-base"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 group-hover:rotate-90 transition-transform duration-200" />
            <span>Fermer</span>
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default RequestDetailsModal;
